import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { Switch } from "../components/ui/switch";

export default function SettingsTab() {
  const [s, setS] = useState({ notify_enabled: true, notify_email: "", ga4_id: "", gtm_id: "", meta_pixel_id: "" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const sendSummary = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/send-summary");
      toast.success(`Lead summary sent to ${r.data?.to ?? "your email"}`);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data ?? {})).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-settings-form" className="max-w-xl rounded-2xl border border-navy/10 bg-white p-6 sm:p-8">
      <h2 className="font-display text-3xl font-extrabold uppercase text-navy">Email notifications</h2>
      <p className="mt-1 text-sm text-slate-600">Get an email whenever someone submits an enquiry on the website.</p>
      <label className="mt-6 flex items-center gap-3 text-sm font-semibold text-navy">
        <Switch data-testid="settings-notify-toggle" checked={!!s.notify_enabled} onCheckedChange={(v) => setS({ ...s, notify_enabled: v })} />
        Send email notifications
      </label>
      <label className="mt-5 block">
        <span className="mb-1.5 block text-sm font-semibold text-navy">Notification email</span>
        <input data-testid="settings-notify-email" type="email" value={s.notify_email ?? ""} onChange={(e) => setS({ ...s, notify_email: e.target.value })} className="field" />
      </label>
      <div data-testid="settings-summary-box" className="mt-6 rounded-xl border border-navy/10 bg-[var(--paper)] p-4">
        <p className="text-sm font-semibold text-navy">Lead summary email</p>
        <p className="mt-1 text-sm text-slate-600">Sent automatically every day at 6:00 PM, plus a weekly sales report every Monday at 9:00 AM. Uses the saved email above.</p>
        <button type="button" onClick={sendSummary} disabled={sending} data-testid="settings-send-summary-button" className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send summary now
        </button>
      </div>
      <h2 className="mt-10 font-display text-3xl font-extrabold uppercase text-navy">Tracking &amp; remarketing</h2>
      <p className="mt-1 text-sm text-slate-600">Paste your IDs to switch these on. They load only for visitors who accept cookies.</p>
      {[["ga4_id", "Google Analytics 4 Measurement ID", "G-XXXXXXXXXX"], ["gtm_id", "Google Tag Manager Container ID", "GTM-XXXXXXX"], ["meta_pixel_id", "Meta Pixel ID", "Numbers only"]].map(([k, l, ph]) => (
        <label key={k} className="mt-5 block">
          <span className="mb-1.5 block text-sm font-semibold text-navy">{l}</span>
          <input data-testid={`settings-${k}`} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value.trim() })} placeholder={ph} className="field" />
        </label>
      ))}
      <button data-testid="settings-save-button" disabled={saving} className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
      </button>
    </form>
  );
}
